import React, { useState, useMemo } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { GRID_SIZE } from '../../constants/grid';
import {
  JOIST_SIZES,
  JOIST_SPACING_OPTIONS,
  LUMBER_SPECIES,
  GRADE_OPTIONS,
  IRC_SPAN_TABLE,
  ENGINEERED_LUMBER,
} from '../../constants/structural';

const pixelsToInches = (px) => px / (GRID_SIZE / 6);

const formatFeetInches = (totalInches) => {
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return inches > 0 ? `${feet}'-${inches}"` : `${feet}'-0"`;
};

const JoistEditor = ({ joist, onUpdate, onDelete, onClose, isMobile = false }) => {
  const [minimized, setMinimized] = useState(isMobile);

  if (!joist) return null;

  const width = Math.abs(joist.endX - joist.startX);
  const height = Math.abs(joist.endY - joist.startY);
  const spanPx = joist.direction === 'horizontal' ? width : height;
  const spanInches = pixelsToInches(spanPx);
  const perpPx = joist.direction === 'horizontal' ? height : width;
  const perpInches = pixelsToInches(perpPx);

  // IRC span check
  const spanCheck = useMemo(() => {
    if (joist.engineered) return null;
    const speciesTable = IRC_SPAN_TABLE[joist.species];
    if (!speciesTable) return null;
    const gradeTable = speciesTable[joist.grade];
    if (!gradeTable) return null;
    const sizeTable = gradeTable[joist.size];
    if (!sizeTable) return null;
    const spacingTable = sizeTable[joist.spacing];
    if (!spacingTable) return null;
    const maxSpan = spacingTable.living;
    if (!maxSpan) return null;
    return { maxSpan, overspan: spanInches > maxSpan };
  }, [joist.species, joist.grade, joist.size, joist.spacing, joist.engineered, spanInches]);

  // Count joists in bay
  const spacingPx = joist.spacing * (GRID_SIZE / 6);
  const joistCount = Math.floor(perpInches / joist.spacing);

  const sectionStyle = {
    marginBottom: '8px',
  };

  const labelStyle = {
    color: '#6080a0',
    fontSize: '10px',
    marginBottom: '3px',
    display: 'block',
  };

  const selectStyle = {
    width: '100%',
    padding: '5px 6px',
    background: '#1a1f24',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '11px',
    cursor: 'pointer',
    outline: 'none',
  };

  const radioGroupStyle = {
    display: 'flex',
    gap: '4px',
  };

  const radioStyle = (active) => ({
    flex: 1,
    padding: '5px 4px',
    background: active ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '4px',
    color: active ? '#4caf50' : '#8090a0',
    fontSize: '10px',
    fontWeight: active ? '600' : '400',
    cursor: 'pointer',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  });

  return (
    <FloatingPanel
      title="Joist Bay"
      onClose={onClose}
      x={isMobile ? 0 : 20}
      y={isMobile ? 'auto' : 200}
      width={isMobile ? '100%' : 220}
      minimized={minimized}
      onToggleMinimize={() => setMinimized(!minimized)}
      isMobile={isMobile}
    >
      {/* Span Info */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ color: '#8090a0', fontSize: '10px' }}>Span</span>
          <span style={{ color: '#fff', fontSize: '12px', fontWeight: '600' }}>
            {formatFeetInches(spanInches)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ color: '#8090a0', fontSize: '10px' }}>Bay Width</span>
          <span style={{ color: '#fff', fontSize: '12px' }}>
            {formatFeetInches(perpInches)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ color: '#8090a0', fontSize: '10px' }}>Joist Count</span>
          <span style={{ color: '#fff', fontSize: '12px' }}>~{joistCount}</span>
        </div>
        {spanCheck && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 6px',
            background: spanCheck.overspan ? 'rgba(244,67,54,0.15)' : 'rgba(76,175,80,0.15)',
            border: `1px solid ${spanCheck.overspan ? 'rgba(244,67,54,0.3)' : 'rgba(76,175,80,0.3)'}`,
            borderRadius: '4px',
            marginTop: '4px',
          }}>
            <span style={{ color: spanCheck.overspan ? '#f44336' : '#4caf50', fontSize: '10px', fontWeight: '600' }}>
              {spanCheck.overspan ? 'OVERSPAN' : 'OK'}
            </span>
            <span style={{ color: '#8090a0', fontSize: '9px' }}>
              Max: {formatFeetInches(spanCheck.maxSpan)}
            </span>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }} />

      {/* Joist Size */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Joist Size</span>
        <select
          value={joist.engineered ? '' : joist.size}
          onChange={(e) => onUpdate({ size: e.target.value, engineered: null })}
          style={selectStyle}
          disabled={!!joist.engineered}
        >
          {Object.entries(JOIST_SIZES).map(([key, s]) => (
            <option key={key} value={key}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Engineered toggle */}
      <div style={sectionStyle}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!joist.engineered}
            onChange={(e) => {
              if (e.target.checked) {
                onUpdate({ engineered: { type: 'i_joist', size: '11.88' } });
              } else {
                onUpdate({ engineered: null });
              }
            }}
            style={{ accentColor: '#4caf50' }}
          />
          <span style={{ color: '#8090a0', fontSize: '10px' }}>Engineered Lumber</span>
        </label>
        {joist.engineered && (
          <div style={{ marginTop: '4px', display: 'flex', gap: '4px' }}>
            <select
              value={joist.engineered.type}
              onChange={(e) => {
                const type = e.target.value;
                const sizes = Object.keys(ENGINEERED_LUMBER[type]?.sizes || {});
                onUpdate({ engineered: { type, size: sizes[0] || '' } });
              }}
              style={{ ...selectStyle, flex: 1 }}
            >
              {Object.entries(ENGINEERED_LUMBER).map(([key, eng]) => (
                <option key={key} value={key}>{eng.label.split(' (')[0]}</option>
              ))}
            </select>
            <select
              value={joist.engineered.size}
              onChange={(e) => onUpdate({ engineered: { ...joist.engineered, size: e.target.value } })}
              style={{ ...selectStyle, flex: 1 }}
            >
              {Object.entries(ENGINEERED_LUMBER[joist.engineered.type]?.sizes || {}).map(([key, s]) => (
                <option key={key} value={key}>{s.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Spacing */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Spacing</span>
        <div style={radioGroupStyle}>
          {JOIST_SPACING_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => onUpdate({ spacing: opt.value })}
              style={radioStyle(joist.spacing === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Direction */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Direction</span>
        <div style={radioGroupStyle}>
          <button
            onClick={() => onUpdate({ direction: 'horizontal' })}
            style={radioStyle(joist.direction === 'horizontal')}
          >
            Horizontal →
          </button>
          <button
            onClick={() => onUpdate({ direction: 'vertical' })}
            style={radioStyle(joist.direction === 'vertical')}
          >
            Vertical ↓
          </button>
        </div>
      </div>

      {/* Species & Grade (hidden when engineered) */}
      {!joist.engineered && (
        <>
          <div style={sectionStyle}>
            <span style={labelStyle}>Species</span>
            <select
              value={joist.species}
              onChange={(e) => onUpdate({ species: e.target.value })}
              style={selectStyle}
            >
              {Object.entries(LUMBER_SPECIES).map(([key, sp]) => (
                <option key={key} value={key}>{sp.label}</option>
              ))}
            </select>
          </div>
          <div style={sectionStyle}>
            <span style={labelStyle}>Grade</span>
            <div style={radioGroupStyle}>
              {GRADE_OPTIONS.map(g => (
                <button
                  key={g.value}
                  onClick={() => onUpdate({ grade: g.value })}
                  style={radioStyle(joist.grade === g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }} />

      {/* Display */}
      <div style={sectionStyle}>
        <span style={labelStyle}>Opacity</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            type="range"
            min="10"
            max="100"
            value={Math.round((joist.opacity ?? 0.6) * 100)}
            onChange={(e) => onUpdate({ opacity: parseInt(e.target.value) / 100 })}
            style={{ flex: 1, accentColor: '#4caf50' }}
          />
          <span style={{ color: '#8090a0', fontSize: '10px', minWidth: '28px', textAlign: 'right' }}>
            {Math.round((joist.opacity ?? 0.6) * 100)}%
          </span>
        </div>
      </div>

      <div style={sectionStyle}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={joist.showLabel !== false}
            onChange={(e) => onUpdate({ showLabel: e.target.checked })}
            style={{ accentColor: '#4caf50' }}
          />
          <span style={{ color: '#8090a0', fontSize: '10px' }}>Show Label</span>
        </label>
      </div>

      {/* Delete */}
      <button
        onClick={onDelete}
        style={{
          width: '100%',
          padding: '6px',
          background: 'rgba(244,67,54,0.15)',
          border: '1px solid rgba(244,67,54,0.3)',
          borderRadius: '4px',
          color: '#f44336',
          fontSize: '11px',
          cursor: 'pointer',
          marginTop: '4px',
        }}
      >
        Delete Joist Bay
      </button>
    </FloatingPanel>
  );
};

export default JoistEditor;
